import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseRecapSessionStateResult {
  dailyActivated: boolean;
  dailyDismissed: boolean;
  weeklyActivated: boolean;
  weeklyDismissed: boolean;
  activateDailyRecap: () => void;
  activateWeeklyRecap: () => void;
  deactivateRecap: () => void;
  dismissDailyCTA: () => void;
  dismissWeeklyCTA: () => void;
}

/**
 * Manages per-session recap activation/dismissal state.
 * State is intentionally NOT persisted across page loads.
 * All flags are reset whenever the local date rolls over (e.g. dashboard left
 * open overnight) so the recap CTA fires again on the new day.
 */
export function useRecapSessionState(
  todayStr: string
): UseRecapSessionStateResult {
  const [dailyActivated, setDailyActivated] = useState(false);
  const [dailyDismissed, setDailyDismissed] = useState(false);
  const [weeklyActivated, setWeeklyActivated] = useState(false);
  const [weeklyDismissed, setWeeklyDismissed] = useState(false);

  // Reset all session flags when the local date rolls over.
  const lastDateRef = useRef<string>(todayStr);
  useEffect(() => {
    if (todayStr !== lastDateRef.current) {
      lastDateRef.current = todayStr;
      setDailyActivated(false);
      setDailyDismissed(false);
      setWeeklyActivated(false);
      setWeeklyDismissed(false);
    }
  }, [todayStr]);

  const activateDailyRecap = useCallback(() => {
    setDailyActivated(true);
  }, []);

  const activateWeeklyRecap = useCallback(() => {
    setWeeklyActivated(true);
  }, []);

  const deactivateRecap = useCallback(() => {
    setDailyActivated(false);
    setWeeklyActivated(false);
  }, []);

  const dismissDailyCTA = useCallback(() => {
    setDailyDismissed(true);
  }, []);

  const dismissWeeklyCTA = useCallback(() => {
    setWeeklyDismissed(true);
  }, []);

  return {
    dailyActivated,
    dailyDismissed,
    weeklyActivated,
    weeklyDismissed,
    activateDailyRecap,
    activateWeeklyRecap,
    deactivateRecap,
    dismissDailyCTA,
    dismissWeeklyCTA,
  };
}
